import React from 'react';
import {
  graphql, PageRendererProps, Script
} from 'gatsby';

import {
  HeroSection, SEO, PageSectionComponents
} from '@library/shared-ui/';

interface Props extends PageRendererProps {
  data: Record<string, any>;
  readonly pageContext: {
    slug: string;
    locale: string;
    defaultLocale: string;
    supportedLocales: Record<string, any>
    contentfulSlug: string;
    brand?: string;
  };
}

const ContentfulPageTemplate = ({
  data, pageContext,
}: Props) => {
  const { brand } = pageContext;
  const smartScriptCampaign = React.useMemo(() => data?.contentfulPageTemplate?.smartScriptCampaign, [data?.contentfulPageTemplate]);
  data = {
    ...data, brand,
  };

  const seo = (
    <SEO
      pageTitle={data?.contentfulPageTemplate?.seoPageTitle}
      pageDescription={data?.contentfulPageTemplate?.seoPageDescription}
      domain="https://sezzle.com"
    />
  );

  const heroSection = (
    <HeroSection
      callOut={data?.contentfulPageTemplate?.heroSection?.callOut}
      subNavBar={data?.contentfulPageTemplate?.heroSection?.subNavBar}
      hero={data?.contentfulPageTemplate?.heroSection?.hero}
      backgroundColor={data?.contentfulPageTemplate?.heroSection?.backgroundColor}
    />
  );

  const sections = data?.contentfulPageTemplate?.sections?.map((section) => (
    <PageSectionComponents
      sectionType={section.sys.contentType.sys.id}
      section={section}
      key={section.id}
      pageContext={pageContext}
    />
  ));

  handleReplacements(data);
  return (
    <>
      <input hidden value={smartScriptCampaign} id="smart-script-campaign" readOnly />
      <Script key="appsflyersmartscript" src="/appsflyersmartscript.js" />
      {seo}
      {heroSection}
      {sections}
    </>
  );
};

/**
 * The `handleReplacements` function is used in Contentful to
 * replace a key word with a different value.
 *
 * For the `brand-template` page, it will take data parsed in
 * from the parseCSV function and replace `BRAND` with the brand
 * data, including title, seo, and content.
 *
 * @see parseCSV in `gatsby-node.js`
 */
function handleReplacements(data: Record<string, any>) {
  const replacements = {};
  if (data?.contentfulPageTemplate?.slug === 'brand-template') {
    const brand = data?.brand;
    replacements.BRAND = brand;
  } else {
    const replacementList = data?.contentfulPageTemplate?.replacements;
    if (!replacementList) {
      return;
    }

    for (const index in replacementList) {
      const replacement = replacementList[index];

      if (typeof replacement !== 'string') {
        continue;
      }
      const parts = replacement.split(':');
      if (parts.length != 2) {
        continue;
      }

      replacements[parts[0]] = parts[1];
    }
  }

  templateReplace(data, replacements);
}

function templateReplace(data: Record<string, any>, replacementValues) {
  for (const i in data) {
    if (data[i]) {
      if (typeof (data[i]) === 'object') {
        templateReplace(data[i], replacementValues);
      } else if (typeof (data[i]) === 'string') {
        for (const s in replacementValues) {
          data[i] = data[i].replaceAll(`%${s}%`, replacementValues[s]);
        }
      }
    }
  }
}

export default ContentfulPageTemplate;

export const getAllPagesFromContentful = graphql`
  query Page ($contentfulSlug: String!, $locale: String!) {
    contentfulPageTemplate(slug: {eq: $contentfulSlug}, node_locale: { eq: $locale}) {
    slug
    node_locale
    project
    seoPageTitle
    seoPageDescription
    replacements
    smartScriptCampaign
    navbarMenuSection {
      ...navigationBarAndMenuFields
    }
    footerSection {
      ...footerFields
    }
    heroSection {
      ...heroFields
    }
    sections {
      ...pageSectionFields
    }
  }
}
`;
